import React, { useState } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Lock,
  Shield,
  Bell,
  Eye,
  EyeOff,
  Camera,
  Save,
  Edit,
  Check,
  X,
  Clock,
  Activity,
  Award,
  Settings,
  LogOut,
  Smartphone,
  Globe,
  Briefcase,
  Link as LinkIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/admin/AdminProfile.css";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";

const AdminProfile = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  // Profile State
  const [profileData, setProfileData] = useState({
    fullName: "Admin Rayyan",
    email: "admin.rayyan@techmentorhub.com",
    phone: "+92 300 1234567",
    location: "Gujranwala, Punjab, Pakistan",
    role: "Super Admin",
    department: "Administration",
    joinedDate: "2023-06-01",
    bio: "Experienced administrator managing Tech Mentor Hub platform operations. Passionate about education technology and helping students achieve their goals.",
    website: "www.rayyantech.com",
    linkedin: "linkedin.com/in/adminrayyan",
    timezone: "Asia/Karachi (PKT)",
    language: "English",
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: true,
    emailNotifications: true,
    smsAlerts: false,
    loginAlerts: true,
    sessionTimeout: "30 minutes",
  });

  // Notification Preferences
  const [notificationPrefs, setNotificationPrefs] = useState({
    newUserRegistrations: true,
    mentorApplications: true,
    systemAlerts: true,
    paymentUpdates: true,
    reportSubmissions: true,
    weeklyDigest: false,
    monthlyReport: true,
  });

  // Activity Log
  const activityLog = [
    {
      id: 1,
      action: "Approved mentor application",
      user: "Dr. Kamran Siddiqui",
      time: "2 hours ago",
      type: "approval",
    },
    {
      id: 2,
      action: "Updated system settings",
      user: "Security Config",
      time: "5 hours ago",
      type: "settings",
    },
    {
      id: 3,
      action: "Exported user report",
      user: "Analytics",
      time: "1 day ago",
      type: "export",
    },
    {
      id: 4,
      action: "Resolved user dispute",
      user: "Hassan Raza vs Sara Ali",
      time: "2 days ago",
      type: "dispute",
    },
    {
      id: 5,
      action: "Created new roadmap",
      user: "AI/ML Engineering Path",
      time: "3 days ago",
      type: "creation",
    },
  ];

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  // Stats
  const adminStats = {
    totalActions: 1247,
    usersManaged: 1234,
    mentorsApproved: 145,
    issuesResolved: 89,
  };

  const handleSaveProfile = () => {
    setIsEditing(false);
    alert("Profile updated successfully!");
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    alert("Password changed successfully!");
  };

  const handleProfileImageChange = (e) => {
    alert("Profile image upload functionality");
  };

  const renderProfileTab = () => (
    <div className="profile-tab-content">
      {/* Profile Header */}
      <div className="profile-header-section">
        <div className="profile-image-container">
          <div className="profile-image">
            <span className="profile-initials">AR</span>
          </div>
          <button
            className="change-photo-btn"
            onClick={handleProfileImageChange}
          >
            <Camera size={18} />
          </button>
        </div>

        <div className="profile-header-info">
          <h2>{profileData.fullName}</h2>
          <p className="profile-role">
            {profileData.role} • {profileData.department}
          </p>
          <div className="profile-meta">
            <span>
              <Calendar size={16} />
              Joined {profileData.joinedDate}
            </span>
            <span>
              <Activity size={16} />
              Last active: Just now
            </span>
          </div>
        </div>

        {!isEditing && (
          <button
            className="edit-profile-btn"
            onClick={() => setIsEditing(true)}
          >
            <Edit size={18} />
            Edit Profile
          </button>
        )}
      </div>

      {/* Admin Stats */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <Activity size={24} />
          <div>
            <span className="stat-value">{adminStats.totalActions}</span>
            <span className="stat-label">Total Actions</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <User size={24} />
          <div>
            <span className="stat-value">{adminStats.usersManaged}</span>
            <span className="stat-label">Users Managed</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <Award size={24} />
          <div>
            <span className="stat-value">{adminStats.mentorsApproved}</span>
            <span className="stat-label">Mentors Approved</span>
          </div>
        </div>
        <div className="admin-stat-card">
          <Check size={24} />
          <div>
            <span className="stat-value">{adminStats.issuesResolved}</span>
            <span className="stat-label">Issues Resolved</span>
          </div>
        </div>
      </div>

      {/* Profile Information */}
      <div className="profile-section">
        <h3>
          <User size={20} />
          Personal Information
        </h3>

        <div className="profile-form">
          <div className="form-row">
            <div className="form-group">
              <label>Full Name</label>
              <div className="input-with-icon">
                <User size={18} />
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) =>
                    setProfileData({ ...profileData, fullName: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <div className="input-with-icon">
                <Mail size={18} />
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) =>
                    setProfileData({ ...profileData, email: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone Number</label>
              <div className="input-with-icon">
                <Phone size={18} />
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) =>
                    setProfileData({ ...profileData, phone: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Location</label>
              <div className="input-with-icon">
                <MapPin size={18} />
                <input
                  type="text"
                  value={profileData.location}
                  onChange={(e) =>
                    setProfileData({ ...profileData, location: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Role</label>
              <div className="input-with-icon">
                <Briefcase size={18} />
                <input type="text" value={profileData.role} disabled />
              </div>
            </div>

            <div className="form-group">
              <label>Department</label>
              <div className="input-with-icon">
                <Settings size={18} />
                <input type="text" value={profileData.department} disabled />
              </div>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Bio</label>
            <textarea
              value={profileData.bio}
              onChange={(e) =>
                setProfileData({ ...profileData, bio: e.target.value })
              }
              disabled={!isEditing}
              rows="4"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Website</label>
              <div className="input-with-icon">
                <Globe size={18} />
                <input
                  type="text"
                  value={profileData.website}
                  onChange={(e) =>
                    setProfileData({ ...profileData, website: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>

            <div className="form-group">
              <label>LinkedIn</label>
              <div className="input-with-icon">
                <LinkIcon size={18} />
                <input
                  type="text"
                  value={profileData.linkedin}
                  onChange={(e) =>
                    setProfileData({ ...profileData, linkedin: e.target.value })
                  }
                  disabled={!isEditing}
                />
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="form-actions">
              <button className="cancel-btn" onClick={handleCancelEdit}>
                <X size={18} />
                Cancel
              </button>
              <button className="save-btn" onClick={handleSaveProfile}>
                <Save size={18} />
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Preferences */}
      <div className="profile-section">
        <h3>
          <Settings size={20} />
          Preferences
        </h3>

        <div className="profile-form">
          <div className="form-row">
            <div className="form-group">
              <label>Timezone</label>
              <div className="input-with-icon">
                <Clock size={18} />
                <select
                  value={profileData.timezone}
                  onChange={(e) =>
                    setProfileData({ ...profileData, timezone: e.target.value })
                  }
                  disabled={!isEditing}
                >
                  <option value="Asia/Karachi (PKT)">Asia/Karachi (PKT)</option>
                  <option value="America/New_York (EST)">
                    America/New_York (EST)
                  </option>
                  <option value="Europe/London (GMT)">
                    Europe/London (GMT)
                  </option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Language</label>
              <div className="input-with-icon">
                <Globe size={18} />
                <select
                  value={profileData.language}
                  onChange={(e) =>
                    setProfileData({ ...profileData, language: e.target.value })
                  }
                  disabled={!isEditing}
                >
                  <option value="English">English</option>
                  <option value="Urdu">Urdu</option>
                  <option value="Arabic">Arabic</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="security-tab-content">
      {/* Change Password */}
      <div className="profile-section">
        <h3>
          <Lock size={20} />
          Change Password
        </h3>

        <form className="profile-form" onSubmit={handlePasswordChange}>
          <div className="form-group">
            <label>Current Password</label>
            <div className="input-with-icon password-input">
              <Lock size={18} />
              <input
                type={showOldPassword ? "text" : "password"}
                placeholder="Enter current password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowOldPassword(!showOldPassword)}
              >
                {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>New Password</label>
            <div className="input-with-icon password-input">
              <Lock size={18} />
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter new password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Confirm New Password</label>
            <div className="input-with-icon password-input">
              <Lock size={18} />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" className="save-btn">
            <Lock size={18} />
            Update Password
          </button>
        </form>
      </div>

      {/* Security Settings */}
      <div className="profile-section">
        <h3>
          <Shield size={20} />
          Security Settings
        </h3>

        <div className="security-options">
          <div className="security-option">
            <div className="option-info">
              <Shield size={20} />
              <div>
                <h4>Two-Factor Authentication</h4>
                <p>Add an extra layer of security to your account</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={securitySettings.twoFactorAuth}
                onChange={(e) =>
                  setSecuritySettings({
                    ...securitySettings,
                    twoFactorAuth: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="security-option">
            <div className="option-info">
              <Mail size={20} />
              <div>
                <h4>Email Notifications</h4>
                <p>Receive email alerts for important activities</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={securitySettings.emailNotifications}
                onChange={(e) =>
                  setSecuritySettings({
                    ...securitySettings,
                    emailNotifications: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="security-option">
            <div className="option-info">
              <Smartphone size={20} />
              <div>
                <h4>SMS Alerts</h4>
                <p>Get text messages for critical security events</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={securitySettings.smsAlerts}
                onChange={(e) =>
                  setSecuritySettings({
                    ...securitySettings,
                    smsAlerts: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="security-option">
            <div className="option-info">
              <Activity size={20} />
              <div>
                <h4>Login Alerts</h4>
                <p>Get notified when someone logs into your account</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={securitySettings.loginAlerts}
                onChange={(e) =>
                  setSecuritySettings({
                    ...securitySettings,
                    loginAlerts: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="security-option">
            <div className="option-info">
              <Clock size={20} />
              <div>
                <h4>Session Timeout</h4>
                <p>Automatically log out after period of inactivity</p>
              </div>
            </div>
            <select
              className="timeout-select"
              value={securitySettings.sessionTimeout}
              onChange={(e) =>
                setSecuritySettings({
                  ...securitySettings,
                  sessionTimeout: e.target.value,
                })
              }
            >
              <option value="15 minutes">15 minutes</option>
              <option value="30 minutes">30 minutes</option>
              <option value="1 hour">1 hour</option>
              <option value="2 hours">2 hours</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="notifications-tab-content">
      <div className="profile-section">
        <h3>
          <Bell size={20} />
          Notification Preferences
        </h3>

        <div className="notification-options">
          <div className="notification-option">
            <div className="option-info">
              <User size={20} />
              <div>
                <h4>New User Registrations</h4>
                <p>Get notified when new users sign up</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationPrefs.newUserRegistrations}
                onChange={(e) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    newUserRegistrations: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="notification-option">
            <div className="option-info">
              <Award size={20} />
              <div>
                <h4>Mentor Applications</h4>
                <p>Receive alerts for new mentor applications</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationPrefs.mentorApplications}
                onChange={(e) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    mentorApplications: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="notification-option">
            <div className="option-info">
              <Activity size={20} />
              <div>
                <h4>System Alerts</h4>
                <p>Important system notifications and warnings</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationPrefs.systemAlerts}
                onChange={(e) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    systemAlerts: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="notification-option">
            <div className="option-info">
              <Settings size={20} />
              <div>
                <h4>Payment Updates</h4>
                <p>Notifications about payment transactions</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationPrefs.paymentUpdates}
                onChange={(e) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    paymentUpdates: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="notification-option">
            <div className="option-info">
              <Shield size={20} />
              <div>
                <h4>Report Submissions</h4>
                <p>Get notified about new reports and disputes</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationPrefs.reportSubmissions}
                onChange={(e) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    reportSubmissions: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="notification-option">
            <div className="option-info">
              <Mail size={20} />
              <div>
                <h4>Weekly Digest</h4>
                <p>Receive weekly summary of platform activities</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationPrefs.weeklyDigest}
                onChange={(e) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    weeklyDigest: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="notification-option">
            <div className="option-info">
              <Calendar size={20} />
              <div>
                <h4>Monthly Report</h4>
                <p>Detailed monthly analytics and insights</p>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={notificationPrefs.monthlyReport}
                onChange={(e) =>
                  setNotificationPrefs({
                    ...notificationPrefs,
                    monthlyReport: e.target.checked,
                  })
                }
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivityTab = () => (
    <div className="activity-tab-content">
      <div className="profile-section">
        <h3>
          <Activity size={20} />
          Recent Activity
        </h3>

        <div className="activity-timeline">
          {activityLog.map((activity) => (
            <div key={activity.id} className={`activity-item ${activity.type}`}>
              <div className="activity-icon">
                {activity.type === "approval" && <Check size={18} />}
                {activity.type === "settings" && <Settings size={18} />}
                {activity.type === "export" && <Activity size={18} />}
                {activity.type === "dispute" && <Shield size={18} />}
                {activity.type === "creation" && <Award size={18} />}
              </div>
              <div className="activity-content">
                <h4>{activity.action}</h4>
                <p>{activity.user}</p>
                <span className="activity-time">
                  <Clock size={14} />
                  {activity.time}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="profile-section">
        <h3>
          <LogOut size={20} />
          Session Management
        </h3>

        <div className="session-info">
          <div className="session-card">
            <div className="session-header">
              <Smartphone size={24} />
              <div>
                <h4>Current Session</h4>
                <p>Chrome on Windows • Gujranwala, Pakistan</p>
              </div>
            </div>
            <span className="session-badge active">Active Now</span>
          </div>

          <div className="session-card">
            <div className="session-header">
              <Smartphone size={24} />
              <div>
                <h4>Previous Session</h4>
                <p>Safari on iPhone • Lahore, Pakistan</p>
              </div>
            </div>
            <span className="session-time">2 hours ago</span>
          </div>
        </div>

        <button className="logout-all-btn" onClick={handleLogout}>
          <LogOut size={18} />
          Logout from All Devices
        </button>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="admin-profile-container">
        {/* Header */}
        <div className="profile-page-header">
          <div>
            <h2>My Profile</h2>
            <p className="profile-page-subtitle">
              Manage your account settings and preferences
            </p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="profile-tabs">
          <button
            className={`tab-btn ${activeTab === "profile" ? "active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            <User size={18} />
            Profile
          </button>
          <button
            className={`tab-btn ${activeTab === "security" ? "active" : ""}`}
            onClick={() => setActiveTab("security")}
          >
            <Shield size={18} />
            Security
          </button>
          <button
            className={`tab-btn ${activeTab === "notifications" ? "active" : ""}`}
            onClick={() => setActiveTab("notifications")}
          >
            <Bell size={18} />
            Notifications
          </button>
          <button
            className={`tab-btn ${activeTab === "activity" ? "active" : ""}`}
            onClick={() => setActiveTab("activity")}
          >
            <Activity size={18} />
            Activity
          </button>
        </div>

        {/* Tab Content */}
        <div className="profile-content">
          {activeTab === "profile" && renderProfileTab()}
          {activeTab === "security" && renderSecurityTab()}
          {activeTab === "notifications" && renderNotificationsTab()}
          {activeTab === "activity" && renderActivityTab()}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProfile;
